import React, { useState, useRef, MutableRefObject } from "react";
import Script from "next/script";
import dynamic from "next/dynamic";

const LottieLoader = dynamic(
  () =>
    import("@/components/Molecules/FullScreenLoader").then(
      (module) => module.LottieLoader
    ),
  { ssr: false }
);

import { DesktopContainer, MobileContainer } from "@/components/Atoms/Box";
import SVGSprite from "@/components/Atoms/SVGSprite";
import Button from "@/components/Atoms/Button";

import { clsx } from "@/utils";
import Typography from "@/foundation/Typography/V2";
import theme from "@/foundation/Theme";

import styles from "./FounderCommunityPage.module.scss";

import data from "./data";

interface HowItWorksI {
  icon: string;
  title: string;
  description?: string;
}

interface FormDataI {
  src: string;
  title: string;
}

interface JoinCommunityPageProps {
  h1Tag: string;
  headerCTAText: string;
  heading: string;
  subheading: string;
  welcomePrefix: string;
  welcomeHeading: string;
  welcomeSubheading: string;
  howItWorksTitle: string;
  howItWorks: HowItWorksI[];
  glow1Url: string;
  glow2Url: string;
  formData: FormDataI;
}

const CommunityJoiningPage: React.FC = () => {
  const {
    h1Tag,
    headerCTAText,
    welcomePrefix,
    welcomeHeading,
    welcomeSubheading,
    howItWorksTitle,
    howItWorks,
    formData,
  } = data as JoinCommunityPageProps;

  const formRef = useRef() as MutableRefObject<HTMLDivElement>;

  const [isFormLoading, setIsFormLoading] = useState(true);

  const joinButton = (
    <div className={clsx(styles.joinButton, "join-button")}>
      <Button
        onClick={() =>
          formRef?.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          })
        }
      >
        {headerCTAText}
        <SVGSprite
          icon="icon-arrow-up-right"
          height="20px"
          width="20px"
          style={{ marginLeft: "8px" }}
        />
      </Button>
    </div>
  );

  return (
    <>
      <Script
        src="https://tally.so/widgets/embed.js"
        onReady={() => {
          window?.Tally?.loadEmbeds();
          setIsFormLoading(false);
        }}
      />
      <div className={styles.joinCommunityPageWrapper}>
        <div className={styles.joinCommunityPage}>
          <div className={clsx(styles.header, "container")}>
            <div className={styles.logo}>
              <Typography variant="routelink1" to="/">
                <DesktopContainer>
                  <svg
                    width="75"
                    height="32"
                    viewBox="0 0 75 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M11.9472 8.24508C10.7862 7.24882 9.26924 6.75069 7.39626 6.75069C5.54731 6.69498 3.73896 7.29964 2.29539 8.45629C1.64974 8.94091 1.12568 9.5691 0.764645 10.2912C0.40361 11.0132 0.215502 11.8094 0.215193 12.6167C0.208601 13.7427 0.585228 14.8374 1.28319 15.721C1.95085 16.6157 2.90411 17.2554 3.98506 17.5342L7.10535 18.3432C8.4802 18.7019 9.16563 19.2917 9.16563 20.1205C9.16289 20.3127 9.11181 20.501 9.01709 20.6681C8.92237 20.8353 8.78706 20.9759 8.62366 21.077C8.24439 21.3453 7.78905 21.485 7.32453 21.4755C6.09714 21.4755 5.4396 20.9295 5.30012 19.8376H0C0.0587026 20.667 0.284129 21.476 0.662788 22.2161C1.04145 22.9563 1.56555 23.6125 2.20374 24.1455C3.48427 25.2081 5.1208 25.7395 7.11332 25.7395C9.0213 25.806 10.8914 25.1963 12.3935 24.0179C13.0543 23.516 13.5894 22.8674 13.9566 22.1232C14.3239 21.3791 14.5132 20.5598 14.5096 19.73C14.5424 18.7397 14.2636 17.764 13.7126 16.9405C13.2825 16.292 12.703 15.7563 12.0229 15.3783C10.8319 14.8479 9.59377 14.4303 8.32478 14.131C6.48634 13.6262 5.56712 12.9753 5.56712 12.1783C5.56712 11.4185 6.12503 11.0386 7.24085 11.0386C8.16538 11.0386 8.67414 11.477 8.76712 12.3537H13.9477C13.9057 11.5699 13.707 10.8025 13.3634 10.0969C13.0198 9.39119 12.5382 8.76153 11.9472 8.24508ZM69.4316 0.0438356H63.7609V6.80249H59.1143V6.04533C59.1117 4.86044 59.6908 4.26799 60.8518 4.26799H61.5293V0H60.6605C58.2535 0.0212536 56.4523 0.49946 55.2568 1.43462C54.0613 2.36978 53.4635 3.83097 53.4635 5.81818V6.81445H46.932L43.2658 17.7694L39.4521 6.81445H33.4147L40.4443 24.7472L37.4595 32H43.4371L51.3674 10.931V11.0705H53.4476V25.6917H59.1143V11.0705H63.7609V25.6917H69.4316V11.0705H74.5803V6.80249H69.4316V0.0438356ZM21.7225 15.8685V25.6917H16.0518V0.0438356H21.7225V9.16563C22.6497 7.60614 24.2995 6.8264 26.672 6.8264C28.8159 6.82374 30.5136 7.50386 31.7649 8.86675C32.5943 9.77728 33.1472 10.9052 33.3589 12.1186C33.5184 13.3458 33.581 14.5836 33.5462 15.8207V25.6917H27.8954V16.0199C27.8954 13.3924 26.8819 12.0787 24.8548 12.0787C22.7666 12.0787 21.7225 13.346 21.7225 15.8685Z"
                      fill="url(#paint0_linear_5479_356127)"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_5479_356127"
                        x1="37.2902"
                        y1="0"
                        x2="37.2902"
                        y2="32"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#5B3EBF" />
                        <stop offset="1" stopColor="#6E4EA3" />
                      </linearGradient>
                    </defs>
                  </svg>
                </DesktopContainer>
                <MobileContainer>
                  <SVGSprite
                    icon="icon-shyft-logo-plain"
                    fill={theme.colors.white}
                  />
                </MobileContainer>
              </Typography>
            </div>
          </div>
          <div className={clsx(styles.contentWrapper, "container")}>
            <div className={clsx(styles.content, "container")}>
              <div className={styles.welcomePrefix}>{welcomePrefix}</div>
              <h1 style={{ display: "none" }}>{h1Tag}</h1>
              <div className={styles.welcomeHeading}>{welcomeHeading}</div>
              <div className={styles.welcomeSubheading}>
                {welcomeSubheading}
              </div>
              <div className={styles.joinButtonWrapper}>{joinButton}</div>
            </div>
            <hr />
            <div className={styles.howItWorksSectionWrapper}>
              <div className={clsx(styles.howItWorksSection, "container")}>
                <div className={styles.howItWorksTitle}>{howItWorksTitle}</div>
                <div className={styles.howItWorks}>
                  {howItWorks?.map(({ icon, title, description }, index) => (
                    <div key={index} className={styles.howItWork}>
                      <div className={styles.icon}>
                        <SVGSprite icon={icon} />
                      </div>
                      <div className={styles.details}>
                        <div className={styles.title}>{title}</div>
                        {description && (
                          <div className={styles.description}>
                            {description}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.formWrapper}>
          <div
            className={clsx(styles.formContainer, "container")}
            ref={formRef}
          >
            {isFormLoading && (
              <div className={styles.loader}>
                <LottieLoader />
              </div>
            )}
            <iframe
              data-tally-src={formData.src}
              loading="eager"
              width="100%"
              height="0"
              frameBorder="0"
              marginHeight={0}
              marginWidth={0}
              title={formData.title}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default CommunityJoiningPage;
